import {
    Dispatch,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
} from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types.ts';
import { selectPlayerConfig } from 'store/player/selectors';

import parseVideoItemForSO from 'services/data-manager/parsing/parseVideoItemForSO.ts';
import { VideoItem } from 'services/data-manager/types.ts';
import useGetPlaybackResumeUavParams from 'services/uav/useGetPlaybackResumeUavParams.ts';
import useGetPlaybackSkipAheadUavParams from 'services/uav/useGetPlaybackSkipAheadUavParams.ts';
import useGetPlaybackSkipBackUavParams from 'services/uav/useGetPlaybackSkipBackUavParams.ts';
import { logUavAnalytics } from 'services/uav/utils.ts';

import isNumber from 'utils/number.ts';

import { OnSkipHandler } from 'screens/PlayerModal/hooks/useSkippings.ts';
import {
    PlayerControlsAccessLevel,
    accessIsRestricted,
} from 'screens/PlayerModal/hooks/useTrickModeControl.ts';
import {
    StartoverStreamType,
    SwitchPlayerSource,
} from 'screens/PlayerModal/types.ts';
import { isStartoverStreamStatic } from 'screens/PlayerModal/utils';

import { PlayerRef } from 'components/Player/types.ts';

type Params = {
    playerRef: RefObject<PlayerRef | null>;
    videoItem: VideoItem | undefined;
    isLive: boolean;
    isCatchup: boolean;
    isStartover: boolean;
    timeshiftAllowed: boolean;
    startoverAllowed: boolean;
    startoverStreamType: StartoverStreamType;
    videoDuration: number;
    playerProgress: number;
    livePosition: number | undefined;
    playerPausedByUser: boolean;
    isSOTSPauseDisabled: boolean;
    rewindAccessLevel: PlayerControlsAccessLevel;
    fastforwardAccessLevel: PlayerControlsAccessLevel;
    skipBackwardDisabled: boolean;
    skipForwardDisabled: boolean;
    templateId?: string;
    railId?: string;
    setSliderIsActive: Dispatch<SetStateAction<boolean>>;
    setReadyToPlay: Dispatch<SetStateAction<boolean>>;
    setPlayerProgress: Dispatch<SetStateAction<number>>;
    setSeeking: Dispatch<SetStateAction<boolean>>;
    handleSwitchPlayerSource: SwitchPlayerSource;
    handleSkip: OnSkipHandler;
    onShowTimeshiftNotAvailableNotification(): void;
    onSetTimeshiftNotification(): void;
    handleJumpToLive(): void;
    onShowControlsRestrictedNotification(): void;
};

function usePlayerProgressBar({
    playerRef,
    videoItem,
    isLive,
    isCatchup,
    isStartover,
    timeshiftAllowed,
    startoverAllowed,
    startoverStreamType,
    videoDuration,
    playerProgress,
    livePosition,
    playerPausedByUser,
    isSOTSPauseDisabled,
    rewindAccessLevel,
    fastforwardAccessLevel,
    skipBackwardDisabled,
    skipForwardDisabled,
    templateId,
    railId,
    setSliderIsActive,
    setReadyToPlay,
    setPlayerProgress,
    setSeeking,
    handleSwitchPlayerSource,
    handleSkip,
    onShowTimeshiftNotAvailableNotification,
    onSetTimeshiftNotification,
    handleJumpToLive,
    onShowControlsRestrictedNotification,
}: Params) {
    const { playbackType } = useSelector(selectPlayerConfig);
    const { getPlaybackResumeUavParams } = useGetPlaybackResumeUavParams({
        playbackType,
        videoItem,
    });
    const { getPlaybackSkipBackUavParams } = useGetPlaybackSkipBackUavParams({
        playbackType,
        videoItem,
    });
    const { getPlaybackSkipAheadUavParams } = useGetPlaybackSkipAheadUavParams({
        playbackType,
        videoItem,
    });
    const [slidingStartPosition, setSlidingStartPosition] = useState<
        number | undefined
    >(undefined);
    const [logEvent, setLogEvent] = useState(false);

    useEffect(() => {
        if (logEvent) {
            logUavAnalytics(
                getPlaybackResumeUavParams({
                    templateId,
                    railId,
                }),
            );
        }

        setLogEvent(false);
    }, [logEvent]);

    const onSlidingValueChanged = useCallback(
        (progress: number) => {
            const slidingBackwardOnLive =
                isLive &&
                slidingStartPosition &&
                slidingStartPosition > progress;

            if (!isLive || slidingBackwardOnLive) {
                setPlayerProgress(progress);
            }
        },
        [isLive, setPlayerProgress, slidingStartPosition],
    );

    const onSlidingStart = useCallback(() => {
        setSliderIsActive(true);

        if (isLive && isSOTSPauseDisabled) {
            onShowTimeshiftNotAvailableNotification();
        } else {
            if (!playerPausedByUser) {
                playerRef.current?.pause();
            }

            setSlidingStartPosition(playerProgress);
        }
    }, [
        setSliderIsActive,
        isLive,
        isSOTSPauseDisabled,
        onShowTimeshiftNotAvailableNotification,
        playerPausedByUser,
        playerProgress,
        playerRef,
    ]);

    const onSlidingCompleted = useCallback(
        async (value: number) => {
            const target = Math.max(0, value);

            if (
                isLive &&
                videoItem &&
                slidingStartPosition &&
                value < slidingStartPosition
            ) {
                if (timeshiftAllowed && !startoverAllowed) {
                    onSetTimeshiftNotification();
                }

                let sourceConfig: VideoItem['sourceConfig'];

                if (isStartoverStreamStatic(startoverStreamType)) {
                    sourceConfig = {
                        startOffset: Math.max(
                            0,
                            value + (videoItem.guardTime || 0),
                        ),
                    };
                } else {
                    sourceConfig = {
                        startOffset: target - (slidingStartPosition || 0),
                    };
                }

                handleSwitchPlayerSource(
                    parseVideoItemForSO(videoItem, sourceConfig),
                    StreamTypes.startover,
                );
                setReadyToPlay(false);
            } else if (
                isStartover &&
                livePosition != null &&
                value >= livePosition
            ) {
                handleJumpToLive();

                if (playerPausedByUser) {
                    setTimeout(() => {
                        setLogEvent(true);
                    }, 500);
                }
            } else if (isStartover || isCatchup) {
                const streamIsLive = await playerRef.current?.getIsLive();

                if (streamIsLive === false) {
                    const duration = await playerRef.current?.getDuration();

                    handleSkip({
                        seekPosition: Math.min(
                            target,
                            duration || videoItem?.duration || 0,
                        ),
                    });
                } else if (streamIsLive && isNumber(slidingStartPosition)) {
                    const timeShiftOffset =
                        await playerRef.current?.getTimeShift();

                    if (isNumber(timeShiftOffset)) {
                        const progressDiff = target - slidingStartPosition;

                        handleSkip({
                            timeShiftOffset: timeShiftOffset + progressDiff,
                        });
                    }
                }
            } else {
                setSeeking(true);
                handleSkip({
                    seekPosition: Math.min(target, videoDuration),
                });

                if (slidingStartPosition) {
                    const slidingEndPosition = Math.floor(value);

                    if (slidingStartPosition < slidingEndPosition) {
                        logUavAnalytics(
                            getPlaybackSkipAheadUavParams({
                                startPosition: slidingStartPosition,
                                endPosition: slidingEndPosition,
                            }),
                        );
                    } else {
                        logUavAnalytics(
                            getPlaybackSkipBackUavParams({
                                startPosition: slidingStartPosition,
                                endPosition: slidingEndPosition,
                            }),
                        );
                    }
                }
            }

            setSlidingStartPosition(undefined);
            setSliderIsActive(false);
        },
        [
            isLive,
            videoItem,
            slidingStartPosition,
            isStartover,
            livePosition,
            isCatchup,
            timeshiftAllowed,
            startoverAllowed,
            startoverStreamType,
            handleSwitchPlayerSource,
            setReadyToPlay,
            onSetTimeshiftNotification,
            handleJumpToLive,
            playerRef,
            handleSkip,
            setSeeking,
            videoDuration,
            getPlaybackSkipAheadUavParams,
            getPlaybackSkipBackUavParams,
            setSliderIsActive,
        ],
    );

    const onSlidingCompleteSilently = useCallback(() => {
        if (
            (accessIsRestricted(rewindAccessLevel) && !skipBackwardDisabled) ||
            (accessIsRestricted(fastforwardAccessLevel) && !skipForwardDisabled)
        ) {
            onShowControlsRestrictedNotification();
        }

        if (!playerPausedByUser) {
            playerRef.current?.play();
        }

        setSlidingStartPosition(undefined);
        setSliderIsActive(false);
    }, [
        rewindAccessLevel,
        skipBackwardDisabled,
        fastforwardAccessLevel,
        skipForwardDisabled,
        playerPausedByUser,
        setSliderIsActive,
        onShowControlsRestrictedNotification,
        playerRef,
    ]);

    return {
        onSlidingValueChanged,
        onSlidingStart,
        onSlidingCompleted,
        onSlidingCompleteSilently,
    };
}

export default usePlayerProgressBar;
